import { useSyncExternalStore } from "react";

export type ThemeChoice = "light" | "dark" | "system";

const STORAGE_KEY = "bergler.theme";
const THEME_VALUES: readonly ThemeChoice[] = ["light", "dark", "system"];

const listeners = new Set<() => void>();

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function readFromStorage(): ThemeChoice {
  if (!isBrowser()) return "light";
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw && (THEME_VALUES as readonly string[]).includes(raw)) {
      return raw as ThemeChoice;
    }
  } catch {
    // localStorage gesperrt (private mode, corp policy) — Fallback auf light.
  }
  return "light";
}

function systemPrefersDark(): boolean {
  if (!isBrowser()) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function resolvedIsDark(choice: ThemeChoice): boolean {
  if (choice === "dark") return true;
  if (choice === "light") return false;
  return systemPrefersDark();
}

function applyTheme(choice: ThemeChoice): void {
  if (!isBrowser()) return;
  const el = document.documentElement;
  if (resolvedIsDark(choice)) {
    el.classList.add("dark");
  } else {
    el.classList.remove("dark");
  }
}

function notify(): void {
  for (const listener of listeners) listener();
}

let current: ThemeChoice = readFromStorage();

export function getTheme(): ThemeChoice {
  return current;
}

export function setTheme(next: ThemeChoice): void {
  if (!(THEME_VALUES as readonly string[]).includes(next)) return;
  current = next;
  if (isBrowser()) {
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // localStorage gesperrt — Choice haelt nur fuer die Session.
    }
    applyTheme(next);
  }
  notify();
}

export function subscribeTheme(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

let systemListenerAttached = false;

export function initTheme(): void {
  if (!isBrowser()) return;
  current = readFromStorage();
  applyTheme(current);

  if (systemListenerAttached) return;
  systemListenerAttached = true;

  const mq = window.matchMedia("(prefers-color-scheme: dark)");
  const onChange = () => {
    if (current === "system") {
      applyTheme("system");
      notify();
    }
  };
  mq.addEventListener("change", onChange);
}

export function useTheme(): ThemeChoice {
  return useSyncExternalStore(subscribeTheme, getTheme, getTheme);
}
